import React, { useState, useEffect, useRef } from 'react';
import styles from './MetricCard.module.css';

/** Super Admin = default. Owner = horizontal icon-left layout (StatisticsSummary style). */
export type MetricCardVariant = 'default' | 'owner';

/** Icon background color: purple/pink (Super Admin), blue/red (Owner cards). */
export type MetricCardIconVariant = 'purple' | 'pink' | 'blue' | 'red';

export interface MetricCardProps {
  value: number | string;
  label: string;
  /** Optional trend text (e.g. "+12%"). Only shown when variant is 'default'. */
  trend?: string;
  trendDirection?: 'up' | 'down';
  /** When false, "up" is red (bad), "down" is green (good). When true, reverse. */
  trendPositiveMeansUp?: boolean;
  icon: React.ReactNode;
  iconVariant?: MetricCardIconVariant;
  /** Layout and styling: 'default' = Super Admin (top row + value + trend), 'owner' = Salon Owner (icon left, label above value). */
  variant?: MetricCardVariant;
  /** Whether to animate number from 0. Default: true for 'default', false for 'owner'. */
  animateValue?: boolean;
  /** Optional class for the card root. */
  className?: string;
  /** Icon size in default layout: 'medium' (24px) or 'large' (40px). Default 'medium'. */
  iconSize?: 'medium' | 'large';
}

const ArrowUp: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 15l-6-6-6 6" />
  </svg>
);

const ArrowDown: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const MetricCard: React.FC<MetricCardProps> = ({
  value,
  label,
  trend,
  trendDirection = 'up',
  trendPositiveMeansUp = true,
  icon,
  iconVariant = 'purple',
  variant = 'default',
  animateValue,
  className,
  iconSize = 'medium',
}) => {
  const shouldAnimate = animateValue ?? (variant === 'default');
  const [displayValue, setDisplayValue] = useState<number | string>(
    typeof value === 'number' && shouldAnimate ? 0 : value
  );
  const hasAnimatedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const lastValueRef = useRef<number | string>(value);

  const positive = trendPositiveMeansUp
    ? trendDirection === 'up'
    : trendDirection === 'down';
  const trendClass = positive ? styles.positive : styles.negative;

  useEffect(() => {
    if (typeof value !== 'number') {
      setDisplayValue(value);
      return;
    }
    if (!shouldAnimate) {
      setDisplayValue(value);
      return;
    }

    const card = cardRef.current;
    if (!card) {
      setDisplayValue(value);
      return;
    }

    if (lastValueRef.current !== value) {
      hasAnimatedRef.current = false;
      setDisplayValue(0);
      lastValueRef.current = value;
    }

    if (hasAnimatedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            const targetValue = value;
            const duration = 1500;
            const startTime = performance.now();
            startTimeRef.current = startTime;

            const animate = (currentTime: number) => {
              if (!startTimeRef.current) return;
              const elapsed = currentTime - startTimeRef.current;
              // Clamp progress to [0, 1]: on some systems the first rAF
              // timestamp can be marginally less than startTime, which would
              // yield a tiny negative easeOut and Math.floor() it to -1.
              const progress = Math.max(0, Math.min(elapsed / duration, 1));
              const easeOut = 1 - Math.pow(1 - progress, 3);
              const currentValue = Math.floor(easeOut * targetValue);
              setDisplayValue(currentValue);
              if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
              } else {
                setDisplayValue(targetValue);
              }
            };

            animationFrameRef.current = requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(card);
    return () => {
      observer.disconnect();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [value, shouldAnimate]);

  const formattedValue =
    typeof displayValue === 'number'
      ? displayValue.toLocaleString()
      : String(displayValue);

  if (variant === 'owner') {
    return (
      <div
        ref={cardRef}
        className={`${styles.card} ${styles.owner} ${className ?? ''}`.trim()}
      >
        <div className={`${styles.iconWrap} ${styles.ownerIcon} ${styles[iconVariant]}`}>
          {icon}
        </div>
        <div className={styles.ownerInfo}>
          <p className={styles.ownerLabel}>{label}</p>
          <h2 className={styles.ownerValue}>{formattedValue}</h2>
        </div>
      </div>
    );
  }

  const iconWrapClass =
    iconSize === 'large'
      ? `${styles.iconWrap} ${styles.iconWrapLarge} ${styles[iconVariant]}`
      : `${styles.iconWrap} ${styles[iconVariant]}`;

  return (
    <div ref={cardRef} className={`${styles.card} ${className ?? ''}`.trim()}>
      <div className={styles.topRow}>
        <div className={iconWrapClass}>{icon}</div>
        <div className={styles.label}>{label}</div>
      </div>
      <div className={styles.valueRow}>
        <span className={styles.value}>{formattedValue}</span>
        {trend != null && trend !== '' && (
          <span className={`${styles.trend} ${trendClass}`}>
            {trendDirection === 'up' ? <ArrowUp /> : <ArrowDown />}
            <span>{trend}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default MetricCard;
